import { lazy, Suspense, useEffect } from 'react';
import { AiConnectProvider } from '../ai/connect/AiProviderContext.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { fetchTransport } from '../ai/connect/fetchTransport.js';
import { isAiLive } from '../ai/connect/mode.ts';
import type { AiProviderMode } from '../ai/connect/select.ts';
import './creditTopupDialog.css';

// The wallet bundle (pricing, payment recovery, modal CSS) loads only when someone actually tops up.
const BuyCreditModal = lazy(() =>
  import('../ai/wallet/BuyCreditModal.js').then((m) => ({ default: m.BuyCreditModal })),
);

/**
 * «افزایش اعتبار» from outside the AI module (e.g. the call modal when the wallet cannot cover an
 * analysis). It is the same purchase flow as the wallet page — same provider, same checkout, same
 * server-side permission checks — mounted on demand. The order is remembered under the wallet's own
 * path so the wallet page can verify/recover it when the gateway returns there.
 */
export function CreditTopupDialog(props: {
  readonly slug: string;
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  // Marks <body> while open so the stylesheet can lift the purchase modal above the call modal.
  useEffect(() => {
    if (!props.open) return undefined;
    document.body.dataset['creditTopup'] = 'open';
    return () => {
      delete document.body.dataset['creditTopup'];
    };
  }, [props.open]);
  if (!props.open) return null;
  const config: AiProviderMode = isAiLive()
    ? { mode: 'live', slug: props.slug, transport: fetchTransport }
    : { mode: 'preview' };
  return (
    <AiConnectProvider config={config}>
      <Suspense fallback={null}>
        <BuyCreditModal
          open
          onClose={props.onClose}
          walletPath={`${tenantBasePath(props.slug)}/ai/wallet`}
        />
      </Suspense>
    </AiConnectProvider>
  );
}
